import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, Textarea, EmptyState, Pagination, FilterBar,
  PageHeader, FilterSelect, ListSkeleton, MonthPicker, StatusBadge, IconButton,
  StickyActions, formatINR, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { SiteSearchPopup } from "../components/SiteSearchPopup";
import { Plus, Pencil, Trash2, Check, X, Wallet, MapPin, Truck, User } from "lucide-react";

const LIMIT = 20;
const EXPENSE_TYPES = ["fuel", "food", "salary", "office_rent", "vehicle_maintenance", "other"];

const TYPE_META = {
  fuel: { icon: "⛽", label: "Fuel", chip: "text-orange-700 bg-orange-50 ring-orange-200" },
  food: { icon: "🍱", label: "Food", chip: "text-emerald-700 bg-emerald-50 ring-emerald-200" },
  salary: { icon: "💵", label: "Salary", chip: "text-brand-700 bg-brand-50 ring-brand-200" },
  office_rent: { icon: "🏠", label: "Office Rent", chip: "text-violet-700 bg-violet-50 ring-violet-200" },
  vehicle_maintenance: { icon: "🔧", label: "Vehicle Maint.", chip: "text-amber-700 bg-amber-50 ring-amber-200" },
  other: { icon: "📦", label: "Other", chip: "text-ink-600 bg-ink-100 ring-ink-200" },
};

const INIT = {
  siteId: "", crewId: "", type: "fuel", amount: "",
  date: new Date().toISOString().split("T")[0],
  description: "", vehicleId: "", fuelLitres: "", fuelRate: "",
  receiptNote: "", status: "approved",
};

const fmtDate = (d) =>
  d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const TypeChip = ({ type }) => {
  const m = TYPE_META[type] || TYPE_META.other;
  return (
    <span className={cx("inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ring-1 ring-inset font-medium whitespace-nowrap", m.chip)}>
      <span>{m.icon}</span>{m.label}
    </span>
  );
};

/* who/what the expense is attached to */
const linkedTo = (exp) => {
  if (exp.type === "salary") return exp.crewId?.name ? { icon: <User size={12} />, text: exp.crewId.name } : null;
  if (["fuel", "vehicle_maintenance"].includes(exp.type)) return exp.vehicleId?.name ? { icon: <Truck size={12} />, text: exp.vehicleId.name } : null;
  return exp.crewId?.name ? { icon: <User size={12} />, text: exp.crewId.name } : null;
};

function ExpenseCard({ exp, onEdit, onDelete, onStatus }) {
  const link = linkedTo(exp);
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 flex-wrap pb-3 mb-3 border-b border-ink-100">
        {exp.status === "pending" && (
          <>
            <Button size="xs" variant="success" icon={<Check size={13} />} onClick={() => onStatus(exp._id, "approved")}>Approve</Button>
            <Button size="xs" variant="dangerSoft" icon={<X size={13} />} onClick={() => onStatus(exp._id, "rejected")}>Reject</Button>
          </>
        )}
        <Button size="xs" variant="outline" icon={<Pencil size={13} />} onClick={() => onEdit(exp)}>Edit</Button>
        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => onDelete(exp._id)} title="Delete" className="ml-auto" />
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <TypeChip type={exp.type} />
          <div className="text-xs text-ink-400 mt-2">{fmtDate(exp.date)}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-ink-900 font-display tnum leading-none">{formatINR(exp.amount)}</div>
          {exp.fuelLitres > 0 && <div className="text-[11px] text-ink-400 mt-1 tnum">{exp.fuelLitres}L × ₹{exp.fuelRate}</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-xs text-ink-500">
        {exp.siteId?.name && (
          <span className="inline-flex items-center gap-1 min-w-0">
            <MapPin size={12} className="text-ink-400 shrink-0" /><span className="truncate max-w-[160px]">{exp.siteId.name}</span>
          </span>
        )}
        {link && <span className="inline-flex items-center gap-1 min-w-0"><span className="text-ink-400">{link.icon}</span><span className="truncate max-w-[140px]">{link.text}</span></span>}
        <span className="text-ink-400">
          by {exp.addedByRole === "crew" && exp.addedBy?.name ? exp.addedBy.name : "Admin"}
        </span>
      </div>

      {exp.description && <p className="text-xs text-ink-500 mt-2 line-clamp-2">{exp.description}</p>}

      <div className="mt-3 pt-3 border-t border-ink-100">
        <StatusBadge status={exp.status || "pending"} />
      </div>
    </Card>
  );
}

export default function AdminExpenses() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [crews, setCrews] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentMonth);
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSite, setFSite] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT, month });
      if (fType) p.set("type", fType);
      if (fStatus) p.set("status", fStatus);
      if (fSite) p.set("siteId", fSite);
      const { data } = await api.get(`/expenses/admin?${p}`);
      setExpenses(data.data.expenses || []);
      setTotal(data.data.total);
      setPages(data.data.pages);
    } catch { toast.error("Failed to load expenses"); }
    finally { setLoading(false); }
  }, [page, month, fType, fStatus, fSite]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/expenses/admin/summary?month=${month}${fSite ? `&siteId=${fSite}` : ""}`);
      setSummary(data.data);
    } catch { /* summary is best-effort */ }
  }, [month, fSite]);

  useEffect(() => {
    Promise.all([api.get("/crews?limit=200"), api.get("/vehicles?limit=200")])
      .then(([c, v]) => {
        setCrews(c.data.data.crews || c.data.data || []);
        setVehicles(v.data.data.vehicles || v.data.data || []);
      })
      .catch(() => { });
  }, []);

  useEffect(() => { fetchExpenses(); fetchSummary(); }, [fetchExpenses, fetchSummary]);

  const openCreate = () => {
    setForm({ ...INIT, date: new Date().toISOString().split("T")[0] });
    setSelected(null);
    setModal("form");
  };

  const openEdit = (e) => {
    setSelected(e);
    setForm({
      siteId: e.siteId?._id || "", crewId: e.crewId?._id || "", type: e.type, amount: e.amount,
      date: e.date, description: e.description || "", vehicleId: e.vehicleId?._id || "",
      fuelLitres: e.fuelLitres || "", fuelRate: e.fuelRate || "",
      receiptNote: e.receiptNote || "", status: e.status || "approved",
    });
    setModal("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        siteId: ["fuel", "food", "other"].includes(form.type) ? form.siteId : "",
        vehicleId: ["fuel", "vehicle_maintenance"].includes(form.type) ? form.vehicleId : "",
        crewId: form.type === "salary" ? form.crewId : "",
        amount: parseFloat(form.amount) || 0,
        fuelLitres: form.fuelLitres ? parseFloat(form.fuelLitres) : undefined,
        fuelRate: form.fuelRate ? parseFloat(form.fuelRate) : undefined,
      };
      if (!selected) { await api.post("/expenses/admin", payload); toast.success("Expense added!"); }
      else { await api.put(`/expenses/admin/${selected._id}`, payload); toast.success("Updated!"); }
      setModal(null);
      fetchExpenses();
      fetchSummary();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/admin/${id}`);
      toast.success("Deleted");
      fetchExpenses(); fetchSummary();
    } catch { toast.error("Failed"); }
  };

  // Status-only endpoint — the full update route would blank siteId/vehicleId/crewId
  const updateStatus = async (id, status) => {
    try {
      await api.put(`/expenses/admin/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      fetchExpenses(); fetchSummary();
    } catch { toast.error("Failed"); }
  };

  const f = k => ({ value: form[k] || "", onChange: e => setForm({ ...form, [k]: e.target.value }) });
  const totalAmt = summary?.total || 0;
  const anyFilter = fType || fStatus || fSite;
  const clearAll = () => { setFType(""); setFStatus(""); setFSite(""); setPage(1); };

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Expenses"
          subtitle={`${total} record${total !== 1 ? "s" : ""} · ${formatINR(totalAmt, { compact: true })} this month`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">Add Expense</Button>}
        />

        {/* ══ Type tiles double as filters ══ */}
        {summary && Object.keys(summary.byType || {}).length > 0 && (
          <div className="shrink-0 grid grid-cols-3 sm:grid-cols-6 gap-2.5 sm:gap-3">
            {Object.entries(summary.byType).map(([type, amt]) => {
              const m = TYPE_META[type] || TYPE_META.other;
              const active = fType === type;
              return (
                <button
                  key={type}
                  onClick={() => { setFType(active ? "" : type); setPage(1); }}
                  aria-pressed={active}
                  className={cx(
                    "text-left bg-white border rounded-2xl p-3 transition-all shadow-card active:scale-[.98]",
                    active ? "border-brand-400 ring-2 ring-brand-100" : "border-ink-200/80 hover:border-ink-300 hover:shadow-raised"
                  )}
                >
                  <div className="text-lg leading-none">{m.icon}</div>
                  <div className="text-base font-bold text-ink-900 font-display tnum mt-2 truncate">{formatINR(amt, { compact: true })}</div>
                  <div className="text-[11px] text-ink-500 truncate">{m.label}</div>
                </button>
              );
            })}
          </div>
        )}

        <FilterBar className="shrink-0">
          <MonthPicker value={month} onChange={v => { setMonth(v); setPage(1); }} className="min-w-[140px]" />
          <FilterSelect value={fType} onChange={v => { setFType(v); setPage(1); }} ariaLabel="Filter by type" className="min-w-[130px]">
            <option value="">All Types</option>
            {EXPENSE_TYPES.map(t => <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>)}
          </FilterSelect>
          <FilterSelect value={fStatus} onChange={v => { setFStatus(v); setPage(1); }} ariaLabel="Filter by status" className="min-w-[130px]">
            <option value="">All Status</option>
            {["pending", "approved", "rejected"].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </FilterSelect>
          <div className="min-w-[200px] flex-1">
            <SiteSearchPopup value={fSite} onChange={(v) => { setFSite(v); setPage(1); }} label="" placeholder="All Sites" />
          </div>
          {anyFilter && (
            <button onClick={clearAll} className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1 whitespace-nowrap">Clear</button>
          )}
        </FilterBar>

        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={8} cols={8} /></div>
        ) : expenses.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<Wallet size={22} />} title="No expenses"
              description="Nothing matches these filters for the selected month."
              action={anyFilter
                ? <Button variant="secondary" onClick={clearAll}>Clear filters</Button>
                : <Button onClick={openCreate} icon={<Plus size={16} />}>Add Expense</Button>} />
          </Card>
        ) : (
          <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(4))}>Actions</th>
                  <th className={cx(thCls, "w-[112px]")}>Date</th>
                  <th className={cx(thCls, "w-[140px]")}>Type</th>
                  <th className={cx(thCls, "w-[124px]")}>Amount</th>
                  {/* site and the crew/vehicle it hangs off share a column so the row fits */}
                  <th className={thCls}>Linked to</th>
                  <th className={thCls}>Details</th>
                  <th className={cx(thCls, "w-[112px]")}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {expenses.map(exp => {
                  const link = linkedTo(exp);
                  return (
                    <tr key={exp._id} className="group hover:bg-ink-50 transition-colors">
                      <td className={cx(tdCls, actionColCls, "px-3")}>
                        <div className="flex gap-0.5">
                          {exp.status === "pending" && (
                            <>
                              <IconButton size="sm" variant="outline" icon={<Check size={16} />} onClick={() => updateStatus(exp._id, "approved")}
                                title="Approve" className="text-emerald-600 hover:bg-emerald-50" />
                              <IconButton size="sm" variant="outline" icon={<X size={16} />} onClick={() => updateStatus(exp._id, "rejected")}
                                title="Reject" className="text-red-600 hover:bg-red-50" />
                            </>
                          )}
                          <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(exp)} title="Edit" />
                          <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(exp._id)} title="Delete" />
                        </div>
                      </td>
                      <td className={cx(tdCls, "text-ink-700 font-medium text-xs")}>
                        <div className="truncate">{fmtDate(exp.date)}</div>
                      </td>
                      <td className={tdCls}><TypeChip type={exp.type} /></td>
                      <td className={tdCls}>
                        <div className="font-bold text-ink-900 tnum truncate">{formatINR(exp.amount)}</div>
                        {exp.fuelLitres > 0 && <div className="text-[11px] text-ink-400 tnum truncate">{exp.fuelLitres}L × ₹{exp.fuelRate}</div>}
                      </td>
                      <td className={cx(tdCls, "text-xs text-ink-500")}>
                        <div className="truncate">{exp.siteId?.name || <span className="text-ink-300">—</span>}</div>
                        {link && (
                          <div className="flex items-center gap-1.5 mt-0.5 text-ink-400 min-w-0">
                            <span className="shrink-0">{link.icon}</span><span className="truncate">{link.text}</span>
                          </div>
                        )}
                      </td>
                      <td className={cx(tdCls, "text-xs")}>
                        {exp.description
                          ? <div className="text-ink-500 truncate" title={exp.description}>{exp.description}</div>
                          : <span className="text-ink-300">—</span>}
                        <div className="text-ink-400 truncate mt-0.5">
                          by {exp.addedByRole === "crew" && exp.addedBy?.name ? exp.addedBy.name : "Admin"}
                        </div>
                      </td>
                      <td className={tdCls}><StatusBadge status={exp.status || "pending"} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsCls}>
              {expenses.map(exp => (
                <ExpenseCard key={exp._id} exp={exp} onEdit={openEdit} onDelete={handleDelete} onStatus={updateStatus} />
              ))}
            </div>
          </ListSurface>
        )}
      </ListPage>

      <Modal
        open={modal === "form"}
        onClose={() => setModal(null)}
        title={selected ? "Edit Expense" : "Add Expense"}
        subtitle={selected ? fmtDate(selected.date) : "Record a new expense"}
        icon={<Wallet size={18} />}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Type" required {...f("type")}>
              {EXPENSE_TYPES.map(t => <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>)}
            </Select>
            <Input label="Amount" type="number" step="0.01" required {...f("amount")} placeholder="0.00" suffix="₹" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Date" type="date" required {...f("date")} />
            {["fuel", "food", "other"].includes(form.type) && (
              <SiteSearchPopup value={form.siteId} onChange={(v) => setForm({ ...form, siteId: v })} label="Site" placeholder="— No Site —" />
            )}
          </div>

          {form.type === "fuel" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 bg-orange-50/70 border border-orange-100 p-3 rounded-2xl">
              <Select label="Vehicle" {...f("vehicleId")} wrapClass="col-span-2 sm:col-span-1">
                <option value="">— None —</option>
                {vehicles.map(v => <option key={v._id} value={v._id}>{v.name}</option>)}
              </Select>
              <Input label="Litres" type="number" step="0.1" {...f("fuelLitres")} placeholder="30" />
              <Input label="Rate / L" type="number" step="0.01" {...f("fuelRate")} placeholder="96.50" suffix="₹" />
            </div>
          )}

          {form.type === "vehicle_maintenance" && (
            <div className="bg-amber-50/70 border border-amber-100 p-3 rounded-2xl">
              <Select label="Vehicle" required {...f("vehicleId")}>
                <option value="">— Select Vehicle —</option>
                {vehicles.map(v => <option key={v._id} value={v._id}>{v.name}</option>)}
              </Select>
            </div>
          )}

          {form.type === "salary" && (
            <div className="bg-brand-50/70 border border-brand-100 p-3 rounded-2xl">
              <Select label="Crew Member" required {...f("crewId")}>
                <option value="">— Select Crew —</option>
                {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {form.type !== "salary" && (
              <Select label="Added By (Crew)" {...f("crewId")}>
                <option value="">Admin</option>
                {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
            )}
            <Select label="Status" {...f("status")} wrapClass={form.type === "salary" ? "sm:col-span-2" : ""}>
              {["pending", "approved", "rejected"].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
          </div>

          <Textarea label="Description / Notes" {...f("description")} placeholder="What was this for?" rows={2} />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">{selected ? "Save Changes" : "Add Expense"}</Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
